import React, { useCallback, useEffect, useState } from 'react'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { Box, Button, EmptyState } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { PageHeader } from 'components/header/PageHeader'
import { Approval } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useApprovalsPreference } from 'hooks/useApprovalsPreference'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePendingApprovalDetails } from 'hooks/usePendingApprovalDetails'
import { ApprovalDecisionStateType, ApprovalStatuses } from 'types/approvals'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { ApprovalContextProvider } from '../../contexts/approvalsContext'
import ApprovalDecisionModal from './components/ApprovalDecisionModal'
import ApprovalHeaderButtons from './components/ApprovalHeaderButtons'
import ApprovalPreferenceSettingsModalV2 from './components/ApprovalPreferenceSettingsModalV2'
import ApprovalPreferenceSuccessModal from './components/ApprovalPreferenceSuccessModal'
import ApprovalTabs from './components/ApprovalTabs'
import { useEnabledApprovalFlows } from './components/hooks/useApprovalsFlowSettings'
import { useCheckUnapprovableTransactions } from './components/hooks/useCheckUnapprovableTransactions'
import ManageApproversModal from './components/ManageApproversModal'

const Approvals = () => {
  const { t } = useTranslation()
  const { tab } = queryString.parse(window.location.search) as { tab?: 'APPROVED' | 'REJECTED' }

  const { currentAccount } = useAccountsContext()
  const {
    profile: { emailAddress: currentEmailAddress },
  } = useProfileContext()
  const permissions = useMappedRoles()?.manageTeamMemberApprovers
  const approvalPermissions = useMappedRoles()?.manageApprovals
  const canEdit = permissions?.canEdit
  const canManageApproval = approvalPermissions?.canEdit

  const currentAccountReference = currentAccount?.reference
  const [showApprovalPreferenceModal, setShowApprovalPreferenceModal] = useState(false)
  const [showApprovalPreferenceSuccessModal, setShowApprovalPreferenceSuccessModal] =
    useState(false)
  const [showManageApproversModal, setShowManageApproversModal] = useState(false)
  const [approvalDecision, setApprovalDecision] = useState<ApprovalDecisionStateType>({
    decision: null,
    showApprovalDecisionModal: false,
    isCompleted: false,
  })
  const [checkedRowData, setCheckedRowData] = useState<unknown[]>([])
  const [activeTab, setActiveTab] = useState(tab || ApprovalStatuses.PENDING)

  const {
    approvalFlowPreferences,
    isApprovalPreferenceError,
    isApprovalPreferenceSuccess,
    isFetchingApprovalPreference,
  } = useApprovalsPreference(currentAccountReference, !!currentAccountReference)

  const enabledApprovalFlowsTranslation = useEnabledApprovalFlows(
    approvalFlowPreferences?.preferences
  )

  const {
    pendingApprovals,
    numberOfPendingApprovals,
    hasPendingApprovals,
    isPendingApprovalsError,
    isPendingApprovalsSuccess,
    isFetchingPendingApprovals,
  } = usePendingApprovalDetails(currentAccountReference, !!currentAccountReference)

  const isError = isApprovalPreferenceError || isPendingApprovalsError
  const isSuccess = isApprovalPreferenceSuccess && isPendingApprovalsSuccess
  const isFetching = isFetchingApprovalPreference || isFetchingPendingApprovals

  const unapprovableTransactions = useCheckUnapprovableTransactions(
    checkedRowData,
    currentEmailAddress
  )

  useEffect(() => {
    setApprovalDecision({ ...approvalDecision, isCompleted: false })
    track.Amp.track(AmplitudeEvent.APPROVALS_HOME, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
      selectedItems: checkedRowData?.length,
    })
  }, [])

  const handleApprovalPreferenceSettingsModal = useCallback(
    ({ isVisible }: { isVisible: boolean }) => {
      setShowApprovalPreferenceModal(isVisible)
    },
    [showApprovalPreferenceModal]
  )

  const handleApprovalPreferenceSuccessModal = useCallback(
    ({ isVisible }: { isVisible?: boolean }) => {
      setShowApprovalPreferenceSuccessModal(isVisible)
    },
    [showApprovalPreferenceSuccessModal]
  )

  const handleManageApproversModal = useCallback(
    ({ isVisible }: { isVisible: boolean }) => {
      setShowManageApproversModal(isVisible)
      isVisible &&
        track.Amp.track(AmplitudeEvent.MANAGE_APPROVERS_INITIATE, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.VIEW,
        })
    },
    [showManageApproversModal]
  )

  const handleApprovalDecisionModal = useCallback(
    ({ isVisible, decision, isCompleted = false }) => {
      setApprovalDecision({
        ...approvalDecision,
        showApprovalDecisionModal: isVisible,
        decision,
        isCompleted,
      })
      const titleOfEventInput =
        decision === Approval.DECISION.APPROVE
          ? AmplitudeEvent?.APPROVE_TRANSACTION_INITIATE
          : AmplitudeEvent?.REJECT_TRANSACTION_INITIATE
      decision &&
        track.Amp.track(titleOfEventInput, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.VIEW,
          selectedItems: checkedRowData?.length,
        })
    },
    [approvalDecision]
  )
  return (
    <ApprovalContextProvider
      permissions={{ canEdit, canManageApproval }}
      pendingApprovalDetails={{ pendingApprovals, numberOfPendingApprovals, hasPendingApprovals }}
      approvalsConfig={{
        approvalPreferences: approvalFlowPreferences?.preferences,
        approvalPreferencesLimits: approvalFlowPreferences?.limits,
        allApprovalFlowsAreDisabled: !!!enabledApprovalFlowsTranslation,
        approvalDecisionIsCompleted: approvalDecision?.isCompleted,
        handleTableRowCheck: value => setCheckedRowData(value),
        handleManageApproversModal,
        handleApprovalDecisionModal,
        handleApprovalPreferenceSettingsModal,
        handleApprovalPreferenceSuccessModal,
      }}
      isFetching={isFetching}
      isSuccess={isSuccess}
      isError={isError}
    >
      <PageHeader
        description={
          hasPendingApprovals || enabledApprovalFlowsTranslation ? (
            <Box className="text-foreground-subdued text-body-regular">
              {enabledApprovalFlowsTranslation
                ? `${t('approvals.enabledOutgoingPayments')} ${enabledApprovalFlowsTranslation}.`
                : t('approvals.disabledApprovalsHeadline')}{' '}
              <Button
                variant="ghost"
                noPadding
                onClick={() => setShowApprovalPreferenceModal(true)}
              >
                {canEdit
                  ? t('approvals.editApprovalPreference')
                  : t('approvals.viewApprovalPreference')}
              </Button>
            </Box>
          ) : (
            t('approvals.defaultPageHeaderDescription')
          )
        }
        renderRightSection={
          hasPendingApprovals && (
            <ApprovalHeaderButtons
              activeTab={activeTab}
              hasCheckedPendingTransactions={!!checkedRowData?.length}
              cannotApprove={unapprovableTransactions}
            />
          )
        }
      />

      {isApprovalPreferenceError && !isFetchingApprovalPreference && (
        <EmptyState
          testId="error-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('oopsSomethingWentWrong')}
        />
      )}

      {!isApprovalPreferenceError && (
        <ApprovalTabs queryTab={tab} activeTab={activeTab} setActiveTab={setActiveTab} />
      )}

      {showManageApproversModal && <ManageApproversModal />}
      {approvalDecision?.showApprovalDecisionModal && (
        <ApprovalDecisionModal
          decision={approvalDecision?.decision}
          selectedItems={checkedRowData}
        />
      )}

      {showApprovalPreferenceModal && (
        <>
          {showApprovalPreferenceSuccessModal ? (
            <ApprovalPreferenceSuccessModal />
          ) : (
            <ApprovalPreferenceSettingsModalV2 />
          )}
        </>
      )}
    </ApprovalContextProvider>
  )
}

export default Approvals
